import { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import toast from 'react-hot-toast';
import { useTree, useSubtree } from '@/hooks/useTree';
import { useTreeStore } from '@/store/useTreeStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useCatalogSearch, useTreeOverlay } from '@/hooks/useMedical';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { EmptyState } from '@/components/common/EmptyState';
import { useTexte } from '@/hooks/useTexte';
import { useFamilyTreeLayout, NODE_W, NODE_H, SYMBOL_W } from './useFamilyTreeLayout';
import { anfang, decke, deckeAuf, vorfahren, vorschlagWurzel, VIELE_PERSONEN } from './aufdecken';
import { legeAhnentafel, istReineAhnenreihe } from './ahnentafel';
import { passeEin } from './einpassen';
import { sprungAnsicht } from './sprung';
import { PersonNode, PersonNodeDefs } from './PersonNode';
import { LinkLayer } from './LinkLayer';
import { PartnershipLayer } from './PartnershipLayer';
import { TreeToolbar } from './TreeToolbar';
import { VorfahrenDialog } from './VorfahrenDialog';
import { OverlayLegend } from './OverlayLegend';
import { TimeSlider } from './TimeSlider';
import { TreeMinimap } from './TreeMinimap';
import { NodeContextMenu, type ContextMenuTarget } from './NodeContextMenu';
import { useTreeKeyboardNav } from './useTreeKeyboardNav';
import { buildNavGraph, type NavPosition } from './treeNavigation';
import { flushSync } from 'react-dom';
import {
  brauchtNeuenBereich,
  mitReserve,
  sichtbarerAusschnitt,
  zuZeichnendeKarten,
  zuZeichnendeLinien,
  zuZeichnendeSymbole,
  type Rechteck,
} from './sichtfenster';
import { downloadTreeSvg } from './exportTreeSvg';
import { printTreePdf } from './printTree';
import { yearRange } from './treeYears';

// ─── Zoom-Grenzen ───────────────────────────────────────────────────────────

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 1.3; // Faktor pro Button-Klick

// ─── TreeCanvas ─────────────────────────────────────────────────────────────

export function TreeCanvas() {
  const { text } = useTexte();
  const selectedPersonId = useTreeStore((s) => s.selectedPersonId);
  const generationDepth = useTreeStore((s) => s.generationDepth);
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);
  const lineageFocusId = useTreeStore((s) => s.lineageFocusId);
  const setLineageFocus = useTreeStore((s) => s.setLineageFocus);
  const coloredLines = useTreeStore((s) => s.coloredLines);
  const activeOverlay = useTreeStore((s) => s.activeOverlay);
  const setActiveOverlay = useTreeStore((s) => s.setActiveOverlay);
  const timeYear = useTreeStore((s) => s.timeYear);
  const setTimeYear = useTreeStore((s) => s.setTimeYear);
  const minimapOpen = useTreeStore((s) => s.minimapOpen);
  const focusRequestId = useTreeStore((s) => s.focusRequestId);
  const clearFocusRequest = useTreeStore((s) => s.clearFocusRequest);
  const setMinimapOpen = useTreeStore((s) => s.setMinimapOpen);
  const setGenerationDepth = useTreeStore((s) => s.setGenerationDepth);
  const erkundung = useTreeStore((s) => s.erkundung);
  const setErkundung = useTreeStore((s) => s.setErkundung);
  const aufgedeckt = useTreeStore((s) => s.aufgedeckt);
  const setAufgedeckt = useTreeStore((s) => s.setAufgedeckt);

  // ─── Medizin-Overlay (Admins oder explizite Medizin-Berechtigung) ──────────
  const canMedical = useAuthStore((s) => s.hasSensitivePermission('medical.view'));
  const canSensitive = useAuthStore((s) => s.hasSensitivePermission('medicalSensitive.view'));
  const { data: catalog } = useCatalogSearch({}, { enabled: canMedical });
  const overlayOptions = useMemo(
    () =>
      (catalog ?? [])
        .filter((c) => canSensitive || !c.isHereditary)
        .map((c) => ({ catalogId: c.id, label: c.label })),
    [catalog, canSensitive],
  );
  const overlayQuery = useTreeOverlay(activeOverlay?.catalogId ?? null);
  const overlay = overlayQuery.data;

  // Fehler (z.B. 403 bei Erbkrankheit ohne medicalSensitive) → Overlay abschalten
  useEffect(() => {
    if (overlayQuery.isError) {
      toast.error('Overlay konnte nicht geladen werden (fehlende Berechtigung?).');
      setActiveOverlay(null);
    }
  }, [overlayQuery.isError, setActiveOverlay]);

  // Wenn Tiefe gesetzt und Person ausgewählt → Teilbaum laden, sonst ganzer Baum
  const useSubtreeMode = generationDepth !== null && selectedPersonId !== null;
  const fullTree = useTree();
  const subtree = useSubtree(useSubtreeMode ? selectedPersonId : null, generationDepth ?? 3);

  const activeQuery = useSubtreeMode ? subtree : fullTree;
  const { data: treeData, isLoading, isError, error, refetch } = activeQuery;

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);

  // Zoom-/Pan-Zustand: die Toolbar braucht den Faktor, die Minimap zusätzlich
  // die Verschiebung, um den sichtbaren Ausschnitt einzuzeichnen.
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const zoomLevel = transform.k;

  const [contextTarget, setContextTarget] = useState<ContextMenuTarget | null>(null);

  /**
   * Zwingt für einen Augenblick alle Karten ins Dokument.
   *
   * Export und Druck bauen ihre Datei aus dem lebenden DOM (`cloneNode`).
   * Ohne diesen Schalter enthielte eine exportierte Datei stillschweigend nur
   * den Ausschnitt, den man beim Klicken gerade vor Augen hatte – ein Verlust,
   * den niemand bemerkt, bis die Datei gebraucht wird.
   */
  const [alleZeichnen, setAlleZeichnen] = useState(false);

  // D3 Zoom-Behavior Ref (persistent über Renders)
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // Container-Größe beobachten
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const uebernimm = (width: number, height: number) => {
      setDimensions((alt) => (alt.width === width && alt.height === height ? alt : { width, height }));
    };

    // Einmal selbst messen. Der Beobachter meldet die Ausgangsgröße nicht
    // verlässlich – gemessen: beim Wechsel von der Suche auf den Baum kam
    // keine einzige Meldung, die Fläche galt als 0×0, und alles was auf die
    // Maße angewiesen ist (Einpassen, Sprung zu einer Person) lief ins Leere.
    const start = container.getBoundingClientRect();
    if (start.width > 0 && start.height > 0) uebernimm(start.width, start.height);

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      uebernimm(width, height);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // ─── Zeit-Schieberegler: Jahres-Bereich ────────────────────────────────────
  const timeBounds = useMemo(() => yearRange(treeData?.nodes ?? []), [treeData]);

  // ─── Erkundungsansicht ─────────────────────────────────────────────────────
  //
  // Sie schiebt sich zwischen die geladenen Daten und das Layout: gezeichnet
  // wird nur der aufgedeckte Ausschnitt. Ist sie aus, geht alles unverändert
  // durch – der ganze Baum, wie bisher.
  // Der Startpunkt wird EINMAL festgelegt, wenn die Ansicht angeht – bei der
  // ausgewählten Person, sonst bei der mit den meisten Verbindungen.
  //
  // Bewusst nicht bei jeder Auswahl neu: sonst verschöbe ein Klick auf eine
  // Person den ganzen Ausschnitt, solange noch nichts aufgedeckt ist, und
  // danach nicht mehr. Zweierlei Verhalten für dieselbe Geste ist schlimmer
  // als das eine oder das andere.
  useEffect(() => {
    if (!erkundung || !treeData || aufgedeckt.size > 0) return;
    const wurzel = selectedPersonId ?? vorschlagWurzel(treeData);
    if (wurzel) setAufgedeckt(anfang(treeData, wurzel));
    // selectedPersonId absichtlich nicht in den Abhängigkeiten: der Startpunkt
    // steht beim Einschalten fest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erkundung, treeData, aufgedeckt.size, setAufgedeckt]);

  const erkundet = useMemo(() => {
    if (!erkundung || !treeData || aufgedeckt.size === 0) return null;
    return deckeAuf(treeData, aufgedeckt);
  }, [erkundung, treeData, aufgedeckt]);

  const anzeigeDaten = erkundet ? erkundet.sichtbar : treeData;

  // Beim Ein- und Ausschalten die Ansicht auf den neuen Ausschnitt stellen.
  //
  // Ohne das blieb der Zoom stehen, wo er war: Wer bei 3 % einen Baum mit 300
  // Personen erkundet, sah anschließend einen Fleck in der Bildmitte und hielt
  // die Ansicht für kaputt. Gemessen, nicht vermutet.
  // Über einen Ref, weil centerView erst weiter unten steht – und die
  // Reihenfolge im Modul nicht umgestellt werden soll, nur damit ein Effekt
  // eine Funktion sieht.
  const centerViewRef = useRef<(() => void) | null>(null);
  // Nur beim EINSCHALTEN: der Ausschnitt ist klein, der alte Zoom passt nicht.
  // Wer bei 3 % einen Baum mit 300 Personen erkundet, sähe sonst einen Fleck
  // in der Bildmitte und hielte die Ansicht für kaputt.
  //
  // Beim Ausschalten bewusst nicht: der Baum ist wieder der, den der Nutzer
  // kennt, und wo er dort steht, hat er selbst entschieden. Für „ich habe mich
  // verlaufen" gibt es den Knopf „Ansicht zentrieren".
  const erkundungVorher = useRef(erkundung);
  const zentrierenOffen = useRef(false);

  useEffect(() => {
    if (erkundungVorher.current === erkundung) return;
    erkundungVorher.current = erkundung;
    if (erkundung) zentrierenOffen.current = true;
  }, [erkundung]);

  const aufdeckenAn = useCallback(
    (personId: string) => {
      if (!treeData) return;
      setAufgedeckt(decke(treeData, aufgedeckt, personId));
    },
    [treeData, aufgedeckt, setAufgedeckt],
  );

  // Vorfahren einer gesuchten Person zeigen. Schaltet die Erkundung bei Bedarf
  // ein und ERSETZT, was bisher aufgedeckt war: Nur eine reine Ahnenreihe lässt
  // sich als Fächer legen. Ergänzt um schon sichtbare Geschwister o. Ä. bliebe
  // es beim allgemeinen Layout (so war es zuerst, und der Fächer griff nie).
  const [vorfahrenOffen, setVorfahrenOffen] = useState(false);
  const [faecherId, setFaecherId] = useState<string | null>(null);
  const vorfahrenAufdecken = useCallback(
    (personId: string) => {
      if (!treeData) return;
      setVorfahrenOffen(false);
      setErkundung(true);
      setAufgedeckt(vorfahren(treeData, personId));
      setSelectedPerson(personId, { openPanel: false });
      setFaecherId(personId);
      zentrierenOffen.current = true;
    },
    [treeData, setErkundung, setAufgedeckt, setSelectedPerson],
  );

  // Solange genau eine Ahnenreihe zu sehen ist, wird sie als Ahnentafel
  // (Fächer) gelegt. Holt man per Klick weitere Verwandte dazu, ist die Menge
  // keine reine Ahnenreihe mehr und das allgemeine Layout übernimmt.
  const faecher = useMemo(() => {
    if (!erkundet || !treeData || !faecherId) return null;
    if (!istReineAhnenreihe(treeData, aufgedeckt, faecherId)) return null;
    return legeAhnentafel(erkundet.sichtbar, faecherId);
  }, [erkundet, treeData, aufgedeckt, faecherId]);

  // Layout berechnen (mit optionaler Fokus-Linie)
  const allgemein = useFamilyTreeLayout(faecher ? undefined : anzeigeDaten, lineageFocusId);
  const { layoutPersons, layoutSymbols, layoutLines } = faecher ?? allgemein;

  // Ab dieser Kartenzahl wird der echte Schlagschatten durch ein
  // nachgezeichnetes Rechteck ersetzt.
  //
  // Ein SVG-`filter` zwingt den Browser für jede Karte einzeln zu einem
  // Zwischenbild. Gemessen an einem Baum mit 3000 Personen: 43 ms je
  // Bildaufbau mit Filter, 6 ms ohne – also 23 gegenüber 160 Bildern in der
  // Sekunde beim Schieben und Zoomen.
  //
  // Die Schwelle liegt dort, wo ohnehin niemand mehr einen weichen Schatten
  // erkennt: Bei mehreren hundert Karten ist so weit herausgezoomt, dass eine
  // Karte wenige Pixel misst. Der Ersatz kostet ein Element statt eines
  // eigenen Zeichendurchgangs.
  const SCHATTEN_GRENZE = 300;
  const echterSchatten = layoutPersons.length <= SCHATTEN_GRENZE;

  // ─── Nur zeichnen, was im Bild ist ───────────────────────────────────────
  //
  // Unterhalb dieser Kartenzahl lohnt der Filter nicht: Das Aussortieren
  // kostet dann mehr, als das Weglassen spart, und kleine Bäume sind ohnehin
  // flüssig.
  const FILTER_AB = 400;
  const filterAktiv = layoutPersons.length >= FILTER_AB && !alleZeichnen;

  const [gezeichneterBereich, setGezeichneterBereich] = useState<Rechteck | null>(null);

  // Der Bereich wird NICHT bei jedem Schieben neu bestimmt, sondern erst, wenn
  // der Blick den bisher gezeichneten Bereich verlässt. Sonst bekäme React bei
  // jedem Bild eine neue Liste und müsste 3000 Elemente abgleichen – das wäre
  // teurer als das, was der Filter einspart.
  useEffect(() => {
    if (!filterAktiv || dimensions.width === 0) return;
    const sicht = sichtbarerAusschnitt(transform, dimensions);
    if (brauchtNeuenBereich(sicht, gezeichneterBereich)) {
      setGezeichneterBereich(mitReserve(sicht));
    }
  }, [transform, dimensions, filterAktiv, gezeichneterBereich]);

  const sichtbarePersonen = useMemo(() => {
    if (!filterAktiv) return layoutPersons;
    return zuZeichnendeKarten(layoutPersons, gezeichneterBereich, NODE_W / 2, NODE_H / 2);
  }, [layoutPersons, gezeichneterBereich, filterAktiv]);

  // Linien und Paarsymbole folgen denselben Grenzen.
  //
  // Im ersten Anlauf wurden nur die Karten gefiltert – die Verbindungslinien
  // blieben vollständig stehen. Bei 3000 Personen sind das über zwölftausend
  // Pfade, und die wurden auch dann gezeichnet, wenn nur eine Handvoll Karten
  // zu sehen war. Der Baum blieb dadurch zäh, gerade beim Hineinzoomen.
  //
  // `griffDurch` koppelt beides an die Zusicherung der Karten: Musste dort auf
  // „alles zeichnen" zurückgefallen werden, weil der Ausschnitt nichts traf,
  // dann bleiben auch Linien und Symbole vollständig. Sonst stünden Karten
  // ohne ihre Verbindungen da – oder umgekehrt.
  const griffDurch = filterAktiv && sichtbarePersonen.length < layoutPersons.length;
  const bereichFuerBeiwerk = griffDurch ? gezeichneterBereich : null;

  const sichtbareLinien = useMemo(
    () => zuZeichnendeLinien(layoutLines, bereichFuerBeiwerk),
    [layoutLines, bereichFuerBeiwerk],
  );

  const sichtbareSymbole = useMemo(
    () => zuZeichnendeSymbole(layoutSymbols, bereichFuerBeiwerk, SYMBOL_W / 2, NODE_H / 2),
    [layoutSymbols, bereichFuerBeiwerk],
  );

  // ─── „Eigene Linie zentrieren" umschalten ──────────────────────────────────
  // An: aktuell ausgewählte Person als Fokus setzen. Aus: Fokus löschen.
  const toggleLineageFocus = useCallback(() => {
    if (lineageFocusId) {
      setLineageFocus(null);
    } else if (selectedPersonId) {
      setLineageFocus(selectedPersonId);
    }
  }, [lineageFocusId, selectedPersonId, setLineageFocus]);

  // Baum anzeigen?
  const showTree = !isLoading && !isError && treeData?.nodes && treeData.nodes.length > 0;

  // ─── D3 Zoom Setup ─────────────────────────────────────────────────────────

  useEffect(() => {
    const svg = svgRef.current;
    const g = gRef.current;
    if (!svg || !g) return;

    const svgSelection = d3.select(svg);

    // Zoom-Behavior erstellen
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([MIN_ZOOM, MAX_ZOOM])
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        d3.select(g).attr('transform', event.transform.toString());
        const { x, y, k } = event.transform;
        setTransform({ x, y, k });
      });

    // Filter: Standard-Pan via Drag (linke Maustaste), Zoom nur via Ctrl+Wheel
    zoom.filter((event: Event) => {
      // Immer Touch-Events erlauben
      if ('touches' in event) return true;

      // Wheel → nur mit Ctrl/Cmd (Pinch-to-Zoom sendet ctrlKey=true)
      if (event.type === 'wheel') {
        return (event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey;
      }

      // Pan nur mit linker Maustaste (rechte/mittlere öffnen Kontextmenü etc.)
      if (event.type === 'mousedown') {
        return (event as MouseEvent).button === 0;
      }

      return true;
    });

    svgSelection.call(zoom);
    // Doppelklick soll Personen-Karten nicht hineinzoomen
    svgSelection.on('dblclick.zoom', null);
    zoomBehaviorRef.current = zoom;

    // Freies Scrollen (Wheel ohne Ctrl) = Pan
    svgSelection.on('wheel.scroll', (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return; // Zoom handled by d3.zoom
      event.preventDefault();

      // Firefox liefert deltaMode=1 (Zeilen) statt Pixel → umrechnen
      const unit = event.deltaMode === 1 ? 33 : event.deltaMode === 2 ? svg.clientHeight : 1;

      const currentTransform = d3.zoomTransform(svg);
      const newTransform = currentTransform.translate(
        (-event.deltaX * unit) / currentTransform.k,
        (-event.deltaY * unit) / currentTransform.k,
      );
      svgSelection.call(zoom.transform, newTransform);
    }, { passive: false });

    return () => {
      svgSelection.on('.zoom', null);
      svgSelection.on('wheel.scroll', null);
    };
  }, [showTree]); // Re-run wenn SVG sichtbar wird

  // ─── Initial zentrieren wenn Daten geladen ────────────────────────────────
  // Nur zentrieren, wenn sich der Personen-Bestand des Baums ändert (initiales
  // Laden, Teilbaum-Wechsel, Person hinzugefügt/entfernt) – NICHT bei bloßen
  // Container-Resizes wie dem Öffnen der Personen-Sidebar, sonst gehen Zoom
  // und Pan bei jeder Personen-Auswahl verloren.

  const treeSignature = useMemo(
    () =>
      (treeData?.nodes ? treeData.nodes.map((n) => n.id).sort().join('|') : '') +
      `#focus=${lineageFocusId ?? ''}`,
    [treeData, lineageFocusId],
  );
  const centeredForRef = useRef('');

  useEffect(() => {
    if (layoutPersons.length === 0 || !svgRef.current || !zoomBehaviorRef.current) return;
    if (dimensions.width === 0 || dimensions.height === 0) return;
    if (centeredForRef.current === treeSignature) return;

    // Wer aus einer Suche kommt, will zu einer bestimmten Person – nicht auf
    // die Gesamtansicht. Steht ein solcher Wunsch an und ist die Person im
    // Layout, überlässt das Einpassen ihm den Vortritt; abgehakt wird der
    // Baum trotzdem, damit er nicht später doch noch einrastet.
    centeredForRef.current = treeSignature;
    if (focusRequestId && layoutPersons.some((p) => p.data.id === focusRequestId)) return;
    centerView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeSignature, layoutPersons.length, dimensions.width, dimensions.height]);

  // ─── Zoom-Aktionen ────────────────────────────────────────────────────────

  const zoomIn = useCallback(() => {
    const svg = svgRef.current;
    const zoom = zoomBehaviorRef.current;
    if (!svg || !zoom) return;

    const svgSel = d3.select(svg);
    svgSel.transition().duration(250).call(zoom.scaleBy, ZOOM_STEP);
  }, []);

  const zoomOut = useCallback(() => {
    const svg = svgRef.current;
    const zoom = zoomBehaviorRef.current;
    if (!svg || !zoom) return;

    const svgSel = d3.select(svg);
    svgSel.transition().duration(250).call(zoom.scaleBy, 1 / ZOOM_STEP);
  }, []);

  const centerView = useCallback(() => {
    const svg = svgRef.current;
    const zoom = zoomBehaviorRef.current;
    if (!svg || !zoom) return;

    // Die Rechnung steht in einem eigenen Modul (einpassen.ts) und ist dort
    // geprüft – sie hat hier zweimal einen Fehler getragen, der erst am
    // laufenden Baum sichtbar wurde. `null` heißt „lässt sich (noch) nicht
    // sinnvoll berechnen"; dann bleibt die Ansicht, wo sie ist.
    const ziel = passeEin(layoutPersons, dimensions);
    if (!ziel) return;

    const transform = d3.zoomIdentity.translate(ziel.x, ziel.y).scale(ziel.k);
    d3.select(svg).transition().duration(400).call(zoom.transform, transform);
  }, [layoutPersons, dimensions]);

  centerViewRef.current = centerView;

  // Nach dem Umschalten einpassen – aber erst, wenn Layout UND Maße stehen.
  // Ein fester Zeitgeber träfe sonst einen Moment, in dem die Fläche noch 0×0
  // groß ist, und genau daraus entstand die kaputte Ansicht.
  useEffect(() => {
    if (!zentrierenOffen.current) return;
    if (layoutPersons.length === 0 || dimensions.width <= 0 || dimensions.height <= 0) return;
    zentrierenOffen.current = false;
    centerView();
  }, [layoutPersons, dimensions, centerView]);

  // ─── Positionen + Verwandtschaft (Tastatur-Navigation, Minimap) ───────────

  const positions = useMemo(() => {
    const map = new Map<string, NavPosition>();
    for (const p of layoutPersons) map.set(p.data.id, { x: p.x, y: p.y });
    return map;
  }, [layoutPersons]);

  const navGraph = useMemo(() => buildNavGraph(treeData?.nodes ?? []), [treeData]);

  // ─── Ansicht auf eine Person / einen Punkt fahren ─────────────────────────

  /**
   * Fährt die Ansicht zu einer Person. Gibt zurück, OB es geklappt hat.
   *
   * Der Rückgabewert ist nicht kosmetisch: der Sprung aus den Suchen kommt
   * an, bevor die Zeichenfläche vermessen ist (`dimensions.width === 0`),
   * wenn man von einer anderen Seite herkommt. Wer den Wunsch dann trotzdem
   * abhakt, verliert ihn – genau das ist passiert: das Panel ging auf, der
   * Baum blieb stehen, und die gesuchte Person war gar nicht im Bild.
   */
  const panToPerson = useCallback(
    (personId: string): boolean => {
      const svg = svgRef.current;
      const zoom = zoomBehaviorRef.current;
      if (!svg || !zoom) return false;
      const node = layoutPersons.find((p) => p.data.id === personId);
      if (!node) return false;

      // Maße aus dem DOM, nicht aus dem Zustand: der Zustand hängt am
      // Beobachter, und der hatte den Sprung schon einmal ins Leere laufen
      // lassen.
      const breite = svg.clientWidth || dimensions.width;
      const hoehe = svg.clientHeight || dimensions.height;
      if (breite === 0 || hoehe === 0) return false;

      const ziel = sprungAnsicht(node, { breite, hoehe }, d3.zoomTransform(svg).k);

      d3.select(svg)
        .transition()
        .duration(450)
        .call(zoom.transform, d3.zoomIdentity.translate(ziel.x, ziel.y).scale(ziel.k));
      return true;
    },
    [layoutPersons, dimensions],
  );

  /** Auswahl setzen und hinfahren. Das Detail-Panel wird nicht aufgeklappt. */
  const goToPerson = useCallback(
    (personId: string) => {
      setSelectedPerson(personId, { openPanel: false });
      panToPerson(personId);
    },
    [setSelectedPerson, panToPerson],
  );

  /** Minimap: Ansicht auf einen Punkt in Baum-Koordinaten zentrieren. */
  const navigateToTreePoint = useCallback(
    (treeX: number, treeY: number) => {
      const svg = svgRef.current;
      const zoom = zoomBehaviorRef.current;
      if (!svg || !zoom || dimensions.width === 0) return;
      const k = d3.zoomTransform(svg).k;
      const tx = dimensions.width / 2 - treeX * k;
      const ty = dimensions.height / 2 - treeY * k;
      // Ohne Übergang: beim Ziehen soll die Ansicht unmittelbar folgen.
      d3.select(svg).call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(k));
    },
    [dimensions],
  );

  // ─── Kontextmenü (Rechtsklick auf eine Karte) ────────────────────────────

  const handleNodeContextMenu = useCallback(
    (personId: string, event: React.MouseEvent) => {
      const node = layoutPersons.find((p) => p.data.id === personId);
      const name = node
        ? `${node.data.firstName} ${node.data.lastName ?? ''}`.trim()
        : 'Person';
      setContextTarget({ personId, personName: name, x: event.clientX, y: event.clientY });
    },
    [layoutPersons],
  );

  const openDetails = useCallback(
    (personId: string) => setSelectedPerson(personId),
    [setSelectedPerson],
  );

  const focusLineageFor = useCallback(
    (personId: string) => setLineageFocus(lineageFocusId === personId ? null : personId),
    [lineageFocusId, setLineageFocus],
  );

  /** Teilbaum ab dieser Person: auswählen und eine Generationen-Tiefe setzen. */
  const setSubtreeRoot = useCallback(
    (personId: string) => {
      setSelectedPerson(personId, { openPanel: false });
      if (generationDepth === null) setGenerationDepth(3);
    },
    [setSelectedPerson, generationDepth, setGenerationDepth],
  );

  // ─── SVG-Export (ganzer Baum, zum großformatigen Ausdrucken) ─────────────

  /**
   * Führt `arbeit` aus, während ausnahmslos alle Karten im Dokument stehen.
   *
   * `flushSync` ist hier kein Schmuck: React würde die Zustandsänderung sonst
   * bündeln und erst nach dieser Funktion zeichnen – der Export klonte dann
   * weiterhin den gefilterten Stand.
   */
  const mitVollstaendigemBaum = useCallback((arbeit: () => void) => {
    flushSync(() => setAlleZeichnen(true));
    try {
      arbeit();
    } finally {
      setAlleZeichnen(false);
    }
  }, []);

  const handleExportSvg = useCallback(() => {
    mitVollstaendigemBaum(() => {
      const svg = svgRef.current;
      const g = gRef.current;
      if (!svg || !g) return;
      try {
        const stamp = new Date().toISOString().slice(0, 10);
        downloadTreeSvg(svg, g, `stammbaum-${stamp}.svg`);
        toast.success('Stammbaum als SVG exportiert.');
      } catch {
        toast.error('SVG-Export fehlgeschlagen.');
      }
    });
  }, [mitVollstaendigemBaum]);

  // ─── PDF-Druck (ganzer Baum, in A4-Seiten gekachelt) ─────────────────────

  const handlePrintPdf = useCallback(() => {
    mitVollstaendigemBaum(() => {
      const svg = svgRef.current;
      const g = gRef.current;
      if (!svg || !g) return;
      const pages = printTreePdf(svg, g);
      if (pages === 0) {
        toast.error('Druckfenster wurde blockiert – bitte Pop-ups für diese Seite erlauben.');
      } else {
        toast.success(`Druckvorschau geöffnet (${pages} Seite${pages === 1 ? '' : 'n'}).`);
      }
    });
  }, [mitVollstaendigemBaum]);

  // ─── Zeitreise ein-/ausschalten ──────────────────────────────────────────
  const toggleTimeTravel = useCallback(() => {
    if (timeYear !== null) {
      setTimeYear(null);
    } else if (timeBounds) {
      setTimeYear(timeBounds.max);
    }
  }, [timeYear, timeBounds, setTimeYear]);

  // ─── Klick auf leere Fläche → Person abwählen ────────────────────────────

  const handleBackgroundClick = useCallback(() => {
    setSelectedPerson(null);
  }, [setSelectedPerson]);

  // ─── Sprung aus den Navbar-Suchen ────────────────────────────────────────
  // Die Schnellsuche und die Suche-Seite landen über /?person=<id> hier. Sobald
  // das Layout die Person kennt, fährt die Ansicht zu ihr.

  useEffect(() => {
    if (!focusRequestId) return;
    if (!layoutPersons.some((p) => p.data.id === focusRequestId)) return;
    // Erst abhaken, wenn die Ansicht wirklich gefahren ist. Solange die
    // Zeichenfläche noch keine Maße hat, bleibt der Wunsch stehen – der
    // Effekt läuft erneut, sobald `dimensions` gesetzt ist.
    if (panToPerson(focusRequestId)) clearFocusRequest();
  }, [focusRequestId, layoutPersons, panToPerson, clearFocusRequest]);

  // ─── Tastatur-Navigation ─────────────────────────────────────────────────

  const deselect = useCallback(() => setSelectedPerson(null), [setSelectedPerson]);
  /** „/" öffnet die globale Schnellsuche (Cmd/Ctrl+K). */
  const openQuickSearch = useCallback(() => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
  }, []);

  useTreeKeyboardNav({
    enabled: !!showTree,
    graph: navGraph,
    positions,
    selectedPersonId,
    onNavigate: goToPerson,
    onOpenDetails: openDetails,
    onDeselect: deselect,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onCenter: centerView,
    onFocusSearch: openQuickSearch,
  });

  // ─── Render ───────────────────────────────────────────────────────────────

  // Container-Div wird IMMER gerendert, damit der ResizeObserver funktioniert.
  // Loading/Error/Empty-Zustände werden als Overlay darüber gerendert.

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-tree-bg">
      {/* Loading */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <LoadingSpinner size="lg" message="Stammbaum wird geladen..." />
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <ErrorMessage
            message={error instanceof Error ? error.message : 'Fehler beim Laden des Stammbaums.'}
            onRetry={() => refetch()}
          />
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && (!treeData || !treeData.nodes || treeData.nodes.length === 0) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <EmptyState
            title={text('app.welcome')}
            description={text('app.welcomeHint')}
          />
        </div>
      )}

      {/* SVG Canvas */}
      {showTree && (
        <>
          <svg
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
            className="block"
            onClick={handleBackgroundClick}
          >
            {/* SVG Styles für Hover-Effekte */}
            <style>{`
              .person-node:hover rect:first-of-type {
                filter: url(#node-shadow-hover);
              }
              .person-node:hover {
                cursor: pointer;
              }
              .person-node:focus {
                outline: none;
              }
              .person-node:focus-visible rect:first-of-type {
                stroke: #3b82f6;
                stroke-width: 2;
                filter: url(#node-shadow-hover);
              }
            `}</style>

            {/* Defs (Shadows, Clips) */}
            <PersonNodeDefs />

            {/* Transformierte Gruppe (Zoom + Pan) */}
            <g ref={gRef}>
              {/* Eltern-Kind-Verbindungslinien (unterste Schicht) */}
              <LinkLayer lines={sichtbareLinien} colored={coloredLines} />

              {/* Paar-Symbole: Ringe (verheiratet), Herz (Partnerschaft), gestrichelt (getrennt) */}
              <PartnershipLayer symbols={sichtbareSymbole} />

              {/* Person-Nodes (obenauf) */}
              {sichtbarePersonen.map((node) => {
                const ov = overlay?.statuses[node.data.id];
                return (
                  <PersonNode
                    key={node.data.id}
                    echterSchatten={echterSchatten}
                    x={node.x}
                    y={node.y}
                    data={node.data}
                    overlayStatus={ov?.status}
                    overlayProbability={ov?.carrierProbability}
                    timeYear={timeYear}
                    onContextMenu={handleNodeContextMenu}
                    verdeckt={erkundet?.verdeckt.get(node.data.id)}
                    onAufdecken={erkundet ? aufdeckenAn : undefined}
                  />
                );
              })}
            </g>
          </svg>

          {/* Zeit-Schieberegler */}
          {timeYear !== null && timeBounds && (
            <TimeSlider
              year={Math.min(Math.max(timeYear, timeBounds.min), timeBounds.max)}
              min={timeBounds.min}
              max={timeBounds.max}
              onChange={setTimeYear}
              onClose={() => setTimeYear(null)}
            />
          )}

          {/* Overlay-Legende */}
          {activeOverlay && overlay && (
            <OverlayLegend overlay={overlay} onClose={() => setActiveOverlay(null)} />
          )}

          {/* Übersichtskarte (unten rechts) */}
          {minimapOpen && (
            <TreeMinimap
              persons={layoutPersons}
              transform={transform}
              viewport={dimensions}
              selectedPersonId={selectedPersonId}
              onNavigate={navigateToTreePoint}
              onClose={() => setMinimapOpen(false)}
            />
          )}

          {/* Kontextmenü einer Personenkarte */}
          {contextTarget && (
            <NodeContextMenu
              target={contextTarget}
              onClose={() => setContextTarget(null)}
              onOpenDetails={openDetails}
              onCenter={goToPerson}
              onFocusLineage={focusLineageFor}
              onSubtreeRoot={setSubtreeRoot}
              lineageActive={lineageFocusId === contextTarget.personId}
            />
          )}

          {/* Toolbar (Zoom-Controls + Zähler + Generationen) */}
          <TreeToolbar
            zoomLevel={zoomLevel}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onCenter={centerView}
            nodeCount={treeData!.nodes.length}
            linkCount={treeData!.links.length}
            generationDepth={generationDepth}
            onGenerationDepthChange={(depth) => useTreeStore.getState().setGenerationDepth(depth)}
            hasSelectedPerson={selectedPersonId !== null}
            lineageActive={lineageFocusId !== null}
            canToggleLineage={lineageFocusId !== null || selectedPersonId !== null}
            onToggleLineage={toggleLineageFocus}
            coloredLines={coloredLines}
            onColoredLinesChange={(on) => useTreeStore.getState().setColoredLines(on)}
            onExportSvg={handleExportSvg}
            onPrintPdf={handlePrintPdf}
            timeActive={timeYear !== null}
            canTimeTravel={timeBounds !== null}
            onToggleTimeTravel={toggleTimeTravel}
            erkundung={erkundung}
            onToggleErkundung={() => setErkundung(!erkundung)}
            onVorfahren={() => setVorfahrenOffen(true)}
            vieleP={(treeData?.nodes.length ?? 0) >= VIELE_PERSONEN}
            sichtbareP={layoutPersons.length}
            minimapOpen={minimapOpen}
            onToggleMinimap={() => setMinimapOpen(!minimapOpen)}
            overlayOptions={canMedical ? overlayOptions : undefined}
            activeOverlayId={activeOverlay?.catalogId ?? null}
            onOverlayChange={setActiveOverlay}
          />
          {vorfahrenOffen && treeData && (
            <VorfahrenDialog
              daten={treeData}
              onClose={() => setVorfahrenOffen(false)}
              onWaehle={vorfahrenAufdecken}
            />
          )}
        </>
      )}
    </div>
  );
}
